/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/// <reference types="edgeclient-callbacks" />

import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import IFormConfiguration, { INPUT } from 'apmui/components/form/Model';
import { findField } from 'apmui/common/modelAPI';
import { CHALLENGE } from './Model';
import edgeClient from 'apmui/plugin/edgeClient';
import IPageModel from '..';
import { divLog } from 'apmui/util/divLog';
import PAGE from '../PageType';
import { PageType } from 'apmui';

const app = App.get();
const client = edgeClient();

const USERNAME = 'username';
const PASSWORD = 'password';
const UI_ONLY_F5_SAVE_PASSWORD_CHECKBOX = '_ui_only_f5_save_password_check_';
const SOFT_TOKEN_ERROR = 'softTokenError';
const F5_CHALLENGE = '_F5_challenge';

let globalTimeoutFlag = false;

/**
 * Detects wether soft token id is valid and != 'password'
 * @param store Logon Page Model
 */
const softTokenIsNotPassword = (store: IPageModel) => {
    if (store.pageType !== PageType.LOGON) {
        return false;
    }
    return store.logon.softToken.fieldName !== PASSWORD;
};

/**
 * Detects wether soft token id is valid and != 'password'
 * @param store Logon Page Model
 */
const softTokenFieldValid = (store: IPageModel) => {
    if (store.pageType !== PageType.LOGON) {
        return false;
    }
    return store.logon.softToken.fieldName && findField(store.logon.form, store.logon.softToken.fieldName);
};
/**
 * Checks whether field at index has proper name and type
 * @param form Form
 * @param index Index of field in form
 * @param type Field type
 * @param name Field name
 */
const fieldCompare = (form: IFormConfiguration, index: number, type: INPUT, name: string) => {
    return form.fields[index] && form.fields[index].type === type && form.fields[index].name === name;
};

/**
 * Detects whether form is suitable for autosubmit or not
 * @param store Logon Page Model
 */
const formCompatible = (store: IPageModel) => {
    if (store.pageType !== 'logon') {
        return false;
    }

    const form = store.logon.form;
    // Cases are: Logon, Password, nonPasswordSoftToken

    let index = 0;
    if (fieldCompare(form, index, INPUT.TEXT, USERNAME)) {
        index++;
    }

    if (fieldCompare(form, index, INPUT.PASSWORD, PASSWORD)) {
        index++;
    }

    if (fieldCompare(form, index, INPUT.CHECKBOX, UI_ONLY_F5_SAVE_PASSWORD_CHECKBOX)) {
        index++;
    }

    if (
        client.hasSoftTokenSupport() &&
        softTokenIsNotPassword(store) &&
        softTokenFieldValid(store) &&
        fieldCompare(form, index, INPUT.PASSWORD, store.logon.softToken.fieldName)
    ) {
        index++;
    }
    divLog(`index ${index} form.fields.length ${form.fields.length}`);
    // fields not found or more fields than needed
    if (index === 0 || form.fields.length > index) {
        return false;
    }

    return true;
};

enum CALLBACK {
    CHALLENGE_AUTOSUBMIT = 'LogonPageChallengeAutoSubmit',
    AUTOSUBMIT = 'LogonPageAutoSubmit',
}

window[CALLBACK.CHALLENGE_AUTOSUBMIT] = function(formId: string) {
    app.dispatch(EventTags.FORM_SUBMIT, formId);
};

window[CALLBACK.AUTOSUBMIT] = function(formId: string) {
    if (client.hasAutoLogon() && client.getAutoLogon()) {
        app.dispatch(EventTags.FORM_SUBMIT, formId);
    }
};

app.subscribe(EventTags.START, (event, store) => {
    if (store.pageType !== PAGE.LOGON) {
        return;
    }
    const logon = store.logon;
    const form = logon.form;

    divLog('1: form.fields=', form.fields.map((f, n) => `${n} ${f.name} '${f.value}'`).join(', '));
    divLog('formCompatible()=', formCompatible(store));
    divLog('softToken.fieldName=', logon.softToken.fieldName);
    divLog('client.available=', client.available());

    // login.inc: ~820
    if (client.available()) {
        // set challenge callback for edgeClient
        client.setCallbacks('', `${CALLBACK.CHALLENGE_AUTOSUBMIT}('${form.id}');`);
        // Check if the softToken.input exists or in case of RSA challenge mode
        const challenge = logon.challenge;
        const retry = logon.retry;
        const softToken = logon.softToken;
        if (softToken.state == '') {
            softToken.state = 'SECURID_AUTH_STATE_START';
        }

        if ((softTokenFieldValid(store) || challenge) && client.hasSoftTokenSupport()) {
            // remap softToken errors to challenge errors
            if (challenge && softToken.message) {
                challenge.message = softToken.message;
                divLog('remapped to softToken', challenge.message);
            }
            // if challenge mode and edgeClient support: set prompt and state
            if (challenge && client.hasSetSoftTokenPrompt()) {
                divLog(`client.setSoftTokenPrompt(${challenge.message})`);
                client.setSoftTokenPrompt(challenge.message);
            }
            // set token state
            divLog(`client.setSoftTokenState(${softToken.state})`);
            client.setSoftTokenState(softToken.state);
            // put fake value into inputSoftToken and disable it, it would be autopopulated
            const inputSoftToken = findField(form, softToken.fieldName);
            if (inputSoftToken && client.canRequestSoftTokenPIN()) {
                inputSoftToken.value = '********';
                inputSoftToken.disabled = true;
            }
        }
        // logon.inc: checkExternalAddCheckbox();
        // support for autologon from Client API via adding checkbox on save password
        if (!formCompatible(store)) {
            // form not compartible show ??? to user
            if (client.hasNotifyUser()) {
                client.notifyUser();
            }
        } else {
            // form is compartible let's try to autopopulate credentials
            // if username is empty put value in it
            const inputUsername = findField(form, USERNAME);
            if (inputUsername && inputUsername.value === '' && client.hasUsername()) {
                inputUsername.value = client.getUsername();
                divLog('set inputUsername length', inputUsername.value.length);
            }
            // can save password
            const inputPassword = findField(form, PASSWORD);
            if (inputPassword && softTokenIsNotPassword(store) && !challenge && !retry) {
                divLog('.isSavePasswordAvailable()=', client.isSavePasswordAvailable());
                if (client.isSavePasswordAvailable() && !inputPassword.value && client.hasPassword()) {
                    inputPassword.value = client.getPassword();
                    divLog('set inputPassword length', inputPassword.value.length);
                    // logon.inc doAutoSubmit
                    if (client.hasAutoLogon()) {
                        client.setCallbacks(
                            `${CALLBACK.AUTOSUBMIT}('${form.id});`,
                            `${CALLBACK.CHALLENGE_AUTOSUBMIT}('${form.id}');`
                        );
                        // and add checkbox that will allow saving password
                        // see submit handler for handling of UI_ONLY_F5_SAVE_PASSWORD_CHECKBOX
                        const value = client.hasSavePasswordChecked() && client.getSavePasswordChecked();
                        divLog('save password checkbox value=', value);
                        form.fields.push({
                            type: INPUT.CHECKBOX,
                            name: UI_ONLY_F5_SAVE_PASSWORD_CHECKBOX,
                            caption: String(logon.form.savePassword),
                            value,
                            default: '1',
                        });

                        divLog('client.getAutoLogon()=', client.getAutoLogon());
                        if (inputPassword.value && client.getAutoLogon()) {
                            // let's autosubmit if possible
                            window[CALLBACK.CHALLENGE_AUTOSUBMIT](form.id);
                        }
                    }
                }
            }
        }
    }
});

app.subscribe(EventTags.FORM_SUBMIT, (event, store) => {
    if (store.pageType !== PAGE.LOGON) {
        return;
    }
    const logon = store.logon;
    const form = logon.form;
    const challenge = logon.challenge;
    const retry = logon.retry;
    // logon.inc MasterSubmit -> OnSubmit
    if (client.available()) {
        if (formCompatible(store)) {
            // save new username in edge client
            const inputUsername = findField(form, USERNAME);
            if (inputUsername && client.hasUsername()) {
                const username = String(inputUsername.value);
                divLog('set inputUsername.value length', username.length);
                client.setUsername(username);
            }
            // save new password in edge client
            const inputPassword = findField(form, PASSWORD);
            if (inputPassword && client.hasPassword()) {
                const password = String(inputPassword.value);
                divLog('set inputPassword.value length=', password.length);
                client.setPassword(password);
            }
            // pass user decision to save the password back to the client
            if (client.hasSavePasswordChecked()) {
                const checkboxSavePassword = findField(form, UI_ONLY_F5_SAVE_PASSWORD_CHECKBOX);
                const savePassword = Boolean(checkboxSavePassword && checkboxSavePassword.value);
                divLog('set checkboxSavePassword=', savePassword);
                client.setSavePasswordChecked(savePassword);
                // disable checkbox as it's not for save afaik
                if (checkboxSavePassword) {
                    checkboxSavePassword.disabled = true;
                }
            }
        }

        // logon.inc OnSubmitEdgeRSASoftToken
        if (client.hasSoftTokenSupport()) {
            const softToken = logon.softToken;
            divLog('softToken.fieldName', softToken.fieldName);
            const inputSoftToken = findField(form, softToken.fieldName);
            if (inputSoftToken) {
                divLog('inputSoftToken.value', inputSoftToken.value);
                // if the client cannot request soft-token PIN,
                // ask user to enter it in the logon page and pass the value to the client
                // Otherwise, the client already has the PIN
                if (!client.canRequestSoftTokenPIN()) {
                    divLog('client.setSoftTokenPIN', inputSoftToken.value);
                    client.setSoftTokenPIN(String(inputSoftToken.value));
                }
                const passcode = client.getSoftTokenPasscode();
                if (passcode) {
                    inputSoftToken.value = passcode;
                }
            } else if (challenge) {
                divLog('logon.challenge', challenge, 'status', challenge.status);
                if (challenge.status === CHALLENGE.PIN_CHANGE && client.hasSoftTokenChallengeResponse()) {
                    const challengeResponse = client.getSoftTokenChallengeResponse();
                    divLog('client.getSoftTokenChallengeResponse()=', challengeResponse);
                    const input = findField(form, F5_CHALLENGE);
                    divLog('input', input && input.type);
                    if (
                        challengeResponse &&
                        input &&
                        (input.type === INPUT.TEXT ||
                            (input.type === INPUT.SELECT &&
                                input.options.filter((x) => x.value === challengeResponse)[0]))
                    ) {
                        input.value = challengeResponse;
                    }
                } else if (challenge.status == CHALLENGE.SOFT_TOKEN) {
                    // soft token case too in case of pin + password
                    const input = form.fields.filter((x) => x.type !== INPUT.SELECT)[0];
                    divLog('input.name', input.name);
                    if (input) {
                        // Request user input by default
                        // TODO: TEST THIS I'm not sure
                        client.setSoftTokenPIN(String(input.value));
                        const passcode = client.getSoftTokenPasscode();
                        if (passcode) {
                            input.value = passcode;
                        }
                    }
                }
            }
            // this field contains status of soft token
            form.fields.push({
                type: INPUT.HIDDEN,
                name: SOFT_TOKEN_ERROR,
                caption: '',
                value: client.getSoftTokenError(),
            });
        }
        // end of OnSubmitEdgeRSASoftToken, OnSubmit
        if (formCompatible(store) && !retry && !challenge && globalTimeoutFlag) {
            // trying to set credentials if they are entered?
            const inputUsername = findField(form, USERNAME);
            const inputPassword = findField(form, PASSWORD);
            if (client.hasAutoLogon()) {
                client.setAutoLogon(!!(inputUsername && inputUsername.value && inputPassword && inputPassword.value));
            }
            // session is being reset here not on timeout
            if (client.hasResetSession()) {
                client.resetSession();
                // reset event
                event.preventDefault();
                event.stopPropagation();
            }
        }
        // end of MasterSubmit
    }
});

// TODO: this controller shouldn't be logon only but rather any not webtop
app.subscribe(EventTags.TIMEOUT, (event, store) => {
    if (store.pageType !== PAGE.LOGON) {
        return;
    }
    // logon.inc sessionTimedOut
    // if edge client - we need to reset session
    divLog('formCompatible()=', formCompatible(store));
    if (client.hasResetSession() && formCompatible(store)) {
        // if form is compartible reset would happen on FORM_SUBMIT, follow timeoutFlag
        globalTimeoutFlag = true;
        // stop propagagtion, don't show splash
        event.preventDefault();
        event.stopPropagation();
        //
        divLog('this event should be last');
    }
});
